import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, EMPTY, Observable, Subscription } from 'rxjs';
import { loadingHttpContext } from 'src/app/common/interceptors/tokens';
import { PendingUserInvolvementInSocialGroup, SocialGroupService } from 'src/app/interfaces/social-groups.service';
import { TableCellOnActionEvent } from 'src/app/interfaces/tables.interfaces';
import { withReplaceBadRequest } from '../../services/api-error';
import {
  CommunityInviteOrDeclineRequest,
  EmptyResponse,
  PaginatablePendingUserInvolvementsInProjects,
  Project,
  Projects,
  RequestCounters,
} from '../../swagger-api';
import { SocialInitiativeActionsEntities } from '../shared/social-initiative/social-initiative-actions-entities.abstract';
import { projectLeadPermissions } from './projects.mock';

export interface InvolvementInSocialGroup {
  items: PendingUserInvolvementInSocialGroup[];
  total_count: number;
}

@Injectable({ providedIn: 'root' })
export class ProjectsService extends SocialInitiativeActionsEntities implements SocialGroupService<Project> {
  private projectsSubject$: BehaviorSubject<Projects> = new BehaviorSubject({
    items: [],
    total_count: 0,
  });

  private pendingRequestsChanges$: BehaviorSubject<InvolvementInSocialGroup> = new BehaviorSubject({
    items: [],
    total_count: 0,
  });
  private pendingInvitesChanges$: BehaviorSubject<InvolvementInSocialGroup> = new BehaviorSubject({
    items: [],
    total_count: 0,
  });

  private projectsCounterChanges$: BehaviorSubject<RequestCounters> = new BehaviorSubject({
    verified_count: 0,
    requests_sent: 0,
    requests_received: 0,
  });

  public get socialGroupChanges(): Observable<Projects> {
    return this.projectsSubject$.asObservable();
  }

  public get pendingRequestsChanges(): Observable<InvolvementInSocialGroup> {
    return this.pendingRequestsChanges$.asObservable();
  }

  public get pendingInvitesChanges(): Observable<InvolvementInSocialGroup> {
    return this.pendingInvitesChanges$.asObservable();
  }

  public get counterChanges(): Observable<RequestCounters> {
    return this.projectsCounterChanges$.asObservable();
  }

  public fetchRequestCounters(): void {
    const subscription: Subscription = this.apiService
      .restSocialProjectsInvolvementCountersGet()
      .pipe(catchError(() => EMPTY))
      .subscribe((counters: RequestCounters) => {
        this.projectsCounterChanges$.next(counters);
        subscription.unsubscribe();
      });
  }

  public fetchUserPersonalRequests(offset?: number, limit?: number, sorting?: string): void {
    this.apiService
      .restSocialProjectsRequestsIndex(offset, limit, sorting, loadingHttpContext())
      .pipe(catchError(() => EMPTY))
      .subscribe((requests: PaginatablePendingUserInvolvementsInProjects) => {
        this.userPersonalProjectRequests = requests.items;

        this.pendingRequestsChanges$.next(requests);
      });
  }

  public fetchUserPersonalInvites(offset?: number, limit?: number, sorting?: string): void {
    this.apiService
      .restSocialProjectsInvitationsIndex(offset, limit, sorting, loadingHttpContext())
      .pipe(catchError(() => EMPTY))
      .subscribe((requests: PaginatablePendingUserInvolvementsInProjects) => {
        this.userProjectInvites = requests.items;

        this.pendingInvitesChanges$.next(requests);
      });
  }

  public onConfirmRequestOrInvite(event: TableCellOnActionEvent): void {
    this.apiService
      .restSocialProjectMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Approved },
        event.entityId,
        event.actorId,
      )
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.updateUserProjectInvites(event.actorId, event.entityId);
        this.requestCounter$.next();
      });
  }

  public onRejectRequestOrInvite(event: TableCellOnActionEvent): void {
    this.apiService
      .restSocialProjectMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Rejected },
        event.entityId,
        event.actorId,
      )
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.updateUserProjectInvites(event.actorId, event.entityId);
        this.requestCounter$.next();
      });
  }

  public onRemoveInvite(event: TableCellOnActionEvent): void {
    this.apiService
      .restSocialProjectMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Rejected },
        event.entityId,
        event.actorId,
      )
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.updateUserPersonalProjectRequests(event.actorId, event.entityId);
        this.requestCounter$.next();
      });
  }

  public confirmRequestOrInvite(event: TableCellOnActionEvent): Observable<EmptyResponse> {
    return this.apiService
      .restSocialProjectMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Approved },
        event.entityId,
        event.actorId,
      )
      .pipe(withReplaceBadRequest({}));
  }

  public rejectRequestOrInvite(event: TableCellOnActionEvent): Observable<EmptyResponse> {
    return this.apiService
      .restSocialProjectMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Rejected },
        event.entityId,
        event.actorId,
      )
      .pipe(catchError(() => EMPTY));
  }

  public removeInvite(event: TableCellOnActionEvent): Observable<EmptyResponse> {
    return this.apiService
      .restSocialProjectMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Rejected },
        event.entityId,
        event.actorId,
      )
      .pipe(catchError(() => EMPTY));
  }

  protected fetchUserEntities(offset?: number, limit?: number): void {
    this.apiService
      .restSocialProjectsIndex(offset, limit, loadingHttpContext())
      .pipe(catchError(() => EMPTY))
      .subscribe((projects: Projects) => {
        projects.items.forEach((post: Project) => (post.permissions = projectLeadPermissions));
        this.projectsSubject$.next(projects);
      });
  }

  /**
   *
   * @param userId {string} - user id
   * @param offset {number} - offset of the first item in the collection to return
   * @param limit {number} - maximum number of entries to return
   */
  protected fetchUserSpecificEntities(userId: string, offset?: number, limit?: number): void {
    this.apiService
      .restSocialUserProjectsIndex(userId, offset, limit, loadingHttpContext())
      .pipe(catchError(() => EMPTY))
      .subscribe((projects: Projects) => {
        this.projectsSubject$.next(projects);
      });
  }

  protected fetchEntityRelatedItemsResolver(entityId: string, offset?: number, limit?: number): void {
    this.apiService
      .restSocialCommunityProjectsIndex(entityId, offset, limit, loadingHttpContext())
      .pipe(catchError(() => EMPTY))
      .subscribe((projects: Projects) => this.projectsSubject$.next(projects));
  }

  protected completeSubject(): void {
    if (this.projectsSubject$) {
      this.projectsSubject$.complete();
    }
  }
}
